import { Avatar } from '@components/avatar';
import { SchedulerSidebarCrewMemberProps } from './types';
import { AppTooltip } from '@components/ui/tooltip/tooltip';
import { Button } from '@components/ui/button';
import { SvgConsumer } from '@components/icons/svgConsumer';
import { useTranslation } from 'react-i18next';
import { cn } from '@utils/cn';
import { useSchedulerSidebar } from '@contexts/scheduler/sidebar';
import { useDropdownsContext } from '@contexts/dropdowns';
import { snakeToCamelCase } from '@utils/textHelpers';
import { useSchedulerContext } from '@contexts/scheduler';
import { useAccess } from '@hooks/access';
import { sortByName } from '../helpers';
import { MultiSelect } from '@components/ui/form/multiSelect';
import { useRef } from 'react';
import { SchedulerConflicts } from '../../conflicts';

export const SchedulerSidebarCrewMember = ({ data }: SchedulerSidebarCrewMemberProps) => {
  const { t } = useTranslation('app');
  const { t: tPrimaryRoles } = useTranslation('app', { keyPrefix: 'primaryRole' });
  const {
    active: { schedule: activeSchedule },
  } = useSchedulerContext();
  const { crewMembers, setState, conflictsByUserId } = useSchedulerSidebar();
  const access = useAccess();
  const popoverRef = useRef<HTMLDivElement>(null);

  const {
    crewMembers: { options: crewMembersOptions, disabled: crewMembersDisabled },
    fieldUsers: { options: fieldUsersOptions, disabled: fieldUsersDisabled },
  } = useDropdownsContext();

  const { userId, name, roles, userDeactivated, conflicts, edit, manuallyAdded, primaryRole } =
    data;
  const liveConflicts = conflictsByUserId[userId] ?? conflicts;
  const alert = !edit && (userDeactivated ? 'error' : liveConflicts.length ? 'warning' : undefined);

  const scheduleMembersOptions = [...crewMembersOptions, ...fieldUsersOptions].filter(
    (el) => el.id !== 'unassigned' && el.id !== activeSchedule.foreman?.id,
  );
  const scheduleMembersOptionsWithDeactivated = userDeactivated
    ? [...scheduleMembersOptions, { id: userId, fullName: name, primaryRole }]
    : scheduleMembersOptions;
  const filteredOptions = scheduleMembersOptionsWithDeactivated
    .filter((option) => option.id === userId || !crewMembers.some((m) => m.userId === option.id))
    .map((option) => ({
      value: option.id,
      label: option.fullName || '',
      sublabel: option.primaryRole
        ? tPrimaryRoles(snakeToCamelCase(String(option.primaryRole)))
        : undefined,
    }));

  const handleEditMember = (selectedUserId: string) => {
    if (!selectedUserId) {
      setState((prev) => ({
        ...prev,
        crewMembers: prev.crewMembers.filter((m) => !!m.userId),
      }));
      return;
    }
    const selected = scheduleMembersOptions.find((el) => el.id === selectedUserId);
    const isInitialMemberSelected = selectedUserId === userId;
    if (isInitialMemberSelected) {
      setState((prev) => ({
        ...prev,
        crewMembers: prev.crewMembers.map((m) => (m.userId === userId ? { ...m, edit: false } : m)),
      }));
    } else {
      setState((prev) => ({
        ...prev,
        defaultCrew: false,
        crewMembers: prev.crewMembers.map((m) =>
          m.userId === userId
            ? {
                id: undefined,
                userId: selectedUserId,
                name: selected?.fullName || '',
                primaryRole: selected?.primaryRole || undefined,
                conflicts: [],
                roles: selected?.roles.map((el) => ({ ...el, id: '' })) || [],
                edit: false,
              }
            : m,
        ),
      }));
    }
  };

  const handleSelectOpenChange = (isOpen: boolean) => {
    if (!isOpen) {
      handleEditMember(userId);
    }
  };

  const handleRemoveMember = () => {
    setState((prev) => ({
      ...prev,
      defaultCrew: userId ? false : prev.defaultCrew,
      crewMembers: prev.crewMembers.filter((m) => m.userId !== userId),
    }));
  };

  const removeButton = () => (
    <AppTooltip side="top" context={t('schedule.crew.tooltip.remove')}>
      <Button
        disabled={access.readOnly}
        variant="ghost"
        className="h-fit p-1.5"
        onClick={handleRemoveMember}>
        <SvgConsumer id="trash" className="shrink-0 text-neutral-400" />
      </Button>
    </AppTooltip>
  );

  if (edit) {
    return (
      <>
        <div className="col-start-1 col-end-4 flex items-center">
          <div ref={popoverRef} className="overflow-visible" />
          <MultiSelect
            value={userId ? [userId] : []}
            onChange={(v) => {
              handleEditMember(v[0] || userId);
            }}
            disabled={
              access.readOnly ||
              crewMembersDisabled ||
              fieldUsersDisabled ||
              filteredOptions.length === 0
            }
            onOpenChange={handleSelectOpenChange}
            className="h-7 px-2.5 text-xs font-medium"
            defaultOpen
            singleMode
            popoverRef={popoverRef}
            options={sortByName(filteredOptions)}
          />
        </div>
        <div className="col-start-4 flex items-center justify-end">{removeButton()}</div>
      </>
    );
  }

  return (
    <>
      <div className="col-start-1 w-fit">
        {alert === 'warning' && (
          <SchedulerConflicts
            name={name}
            title={t('schedule.crew.tooltip.conflict.title')}
            conflicts={liveConflicts}
            iconClassName="text-lg"
          />
        )}
        {alert === 'error' && (
          <AppTooltip context={t('schedule.crew.tooltip.error')}>
            <SvgConsumer id="circleError" className="shrink-0 text-lg text-danger-400" />
          </AppTooltip>
        )}
      </div>
      <div className="col-start-2 flex items-center gap-2.5">
        <div className="relative shrink-0">
          <Avatar
            name={alert === 'error' ? '' : name}
            hideTooltip
            className={cn('h-6 w-6 text-xs', alert === 'error' && 'bg-neutral-200')}>
            {alert === 'error' && (
              <SvgConsumer className="ml-[2px] shrink-0 text-sm text-white" id="removePerson" />
            )}
          </Avatar>
          {manuallyAdded && (
            <AppTooltip side="bottom" context={t('schedule.crew.tooltip.manuallyAdded')}>
              <span className="absolute -bottom-1.5 -right-1.5 flex h-1.5 w-1.5 p-1.5 bg-white items-center justify-center rounded-full p-2">
                <SvgConsumer id="manuallyAdded" className="shrink-0 text-blue-400 text-xs" />
              </span>
            </AppTooltip>
          )}
        </div>
        <div className="flex flex-col">
          <strong
            className={cn(
              'text-xs font-semibold',
              alert === 'error' ? 'text-neutral-300 line-through' : 'text-neutral-600',
            )}>
            {name}
          </strong>
          <span className="text-[10px] leading-3 text-neutral-400">
            {roles?.map((el) => t(`role.${snakeToCamelCase(String(el.name || ''))}`)).join(', ')}
          </span>
        </div>
      </div>
      <div className="col-start-3 flex items-center justify-end">
        <AppTooltip side="top" context={t('schedule.crew.tooltip.replace')}>
          <Button
            disabled={access.readOnly}
            variant="ghost"
            className="h-fit p-1.5"
            onClick={() => {
              if (edit) return;
              setState((prev) => ({
                ...prev,
                crewMembers: prev.crewMembers.map((m) =>
                  m.userId === userId ? { ...m, edit: true } : m,
                ),
              }));
            }}>
            <SvgConsumer id="replacePerson" className="shrink-0 text-neutral-400" />
          </Button>
        </AppTooltip>
      </div>
      <div className="col-start-4 flex items-center justify-end">{removeButton()}</div>
    </>
  );
};
